import dayjs, { Dayjs } from 'dayjs';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import { InvestigateContext } from '../../app-v2/context/investigate-context';
import { useViewContext } from '../../app-v2/context/view-context';
import { EnsembleContext } from '../context/ensemble-context';
import { getEnsembleIdByReference } from '../services/alerts';
import {
  fetchAllHiddenTags,
  getAutoLabeledEvents,
  getEnsembleModels,
  hideTag,
  predictedTagsMinMaxData,
  resampledTagsMinMax,
  resampledTagsMinMaxV2,
  resampledTagsMinMaxDiscrete,
  resampledTagsMinMaxString,
  rollingAverageTagsMinMax,
  rollingAverageWithAnomalies,
  limitTagsMinMax,
  // searchTagsMinMax,
  timeSearch,
  unHideTag,
} from '../services/tag';

import { ANOMALIES_KEY } from './anomalies';
import { ANOMALOUS_FOLDERS_KEY, FOLDER_KEY } from './folders';
import { useTenants } from './tenants';

import { usePrevious } from '@controlrooms/hooks';
import {
  AnomalyRange,
  CRTagData,
  DataModeType,
  Ensemble,
  HiddenTag,
  Mode,
  PredictedDataParams,
  PredictedDataPayload,
  ResampleTagsMinMaxResult,
  RollingAverageWithAnomaliesParams,
  TagMinMaxRollingAveragePayload,
  TagResampleMinMaxPayload,
  TagRollingAverageWithAnomaliesPayload,
  TimeSeries,
  TimeThreshold,
  TransformedPredictedTag,
} from '@controlrooms/models';
import { ObjectUtils } from '@controlrooms/utils';
import { IntervalUtil } from '@controlrooms/utils/src/chart-utils/interval';

export const HIDDEN_TAG_KEY = 'HIDDEN_TAGS';

interface RangeParams {
  startTime: Dayjs | undefined;
  endTime: Dayjs | undefined;
}

interface FolderTag {
  tag_name: string;
  folder: number;
}

interface TagParams extends RangeParams {
  streamingTimeInSeconds?: number | undefined;
  folder?: number;
}

interface TagMinMaxRollingAverageParams {
  tagName: string;
  startTime: string;
  endTime: string;
  windowSizeMinutes: number;
  useHourlyData: boolean;
  viewStartTime: string;
  viewEndTime: string;
  dataType?: DataModeType;
  opMode?: string;
  exclusions?: { start_time: string; end_time: string }[];
}

interface TagMinMaxParams extends TagParams {
  tag: string;
  dataType?: DataModeType;
}
interface FolderTagMinMaxParams extends RangeParams {
  streamingTimeInSeconds?: number | undefined;
  folderTags: FolderTag[];
}

interface TagMinMaxStringParams extends TagParams {
  tag: string[];
  dataType?: DataModeType;
}

interface MultiTagMinMaxParams extends TagParams {
  tag: string | string[];
}

interface MultiTagMinMaxStringParams extends TagParams {
  tag: string | string[];
}

// interface TagSearchParams extends TagParams {
//   searchKeys: string[] | undefined;
// }

interface TagResampleParams extends TagParams {
  tags: string[] | undefined;
  dataType?: DataModeType;
}

export const TAG_DATA_KEY = 'tag_data';
export const FOLDER_DATA_KEY = 'folderData';
export const FOLDER_STRING_DATA_KEY = 'folderStringData';
export const FOLDER_DISCRETE_DATA_KEY = 'folderDiscreteData';
export const NUMERIC_TAGS_DATA_KEY = 'numeric-tags';
export const STRING_TAGS_DATA_KEY = 'string-tags';
export const DISCRETE_TAGS_DATA_KEY = 'discrete-tags';

export const useFolderTagMinMax = (
  { startTime, endTime, streamingTimeInSeconds, folderTags }: FolderTagMinMaxParams,
  enabled = true,
) => {
  const { currentTenant } = useTenants();
  const {
    viewState: { selectedEnsemble: viewEnsemble, dataModeType },
    getQueryKeyWithViewId,
  } = useViewContext();
  const { selectedEnsemble: appEnsemble } = useContext(EnsembleContext);
  const selectedEnsemble = viewEnsemble ?? appEnsemble;
  const tenantConfig = currentTenant?.preferences;
  const isStreaming = Boolean(streamingTimeInSeconds);
  const formattedStartTime = startTime?.toISOString() ?? undefined;
  const formattedEndTime = endTime?.toISOString() ?? undefined;
  const { setTagChartData } = useContext(InvestigateContext);

  let interval = dataModeType === DataModeType.RAW ? 0 : IntervalUtil.DEFAULT_INTERVAL;
  if (startTime && endTime) {
    interval =
      dataModeType === DataModeType.RAW
        ? 0
        : dataModeType === DataModeType.RESAMPLED
        ? Math.max(
            60,
            IntervalUtil.fromThresholds(
              startTime,
              endTime,
              tenantConfig?.analyzeTimeThresholds as Array<TimeThreshold>,
            ),
          )
        : IntervalUtil.fromThresholds(
            startTime,
            endTime,
            tenantConfig?.analyzeTimeThresholds as Array<TimeThreshold>,
          );
  }

  const refetchInterval: number | false = isStreaming
    ? IntervalUtil.refetchMsFromInterval(interval)
    : false;

  const queries = folderTags.map(({ folder, tag_name }) => {
    const payload = {
      folder: Number(folder),
      tag_names: [tag_name],
      interval: interval,
    } as unknown as TagResampleMinMaxPayload;

    if (isStreaming) {
      payload.preset = streamingTimeInSeconds;
    } else {
      payload.start_time = formattedStartTime;
      payload.end_time = formattedEndTime;
      payload.start_time = formattedStartTime;
    }
    if (selectedEnsemble?.family_id) {
      payload.ensemble_family_id = selectedEnsemble?.family_id;
    }
    return {
      queryKey: [
        getQueryKeyWithViewId(FOLDER_DATA_KEY),
        getQueryKeyWithViewId(NUMERIC_TAGS_DATA_KEY),
        folder,
        tag_name,
        selectedEnsemble?.family_id,
      ],
      select: (body: ResampleTagsMinMaxResult) => body.result,
      queryFn: () => resampledTagsMinMax(payload, selectedEnsemble?.family_id as string),
      refetchInterval: refetchInterval, // 60 seconds
      retry: isStreaming ? 1 : undefined,
      enabled:
        enabled &&
        ObjectUtils.areNonNull(payload as unknown as Record<string, unknown>, Object.keys(payload)),
      onSuccess: (newData: CRTagData<TimeSeries, Mode, AnomalyRange>[]) => {
        newData.map((tagData) => {
          setTagChartData((prevState) => {
            return {
              ...prevState,
              ...{
                [tagData.tag]: tagData,
              },
            };
          });
        });
      },
    };
  });

  return useQueries(queries);
};
export const useFolderStringTagMinMax = (
  { startTime, endTime, streamingTimeInSeconds, folderTags }: FolderTagMinMaxParams,
  enabled = true,
) => {
  const { currentTenant } = useTenants();
  const {
    viewState: { selectedEnsemble: viewEnsemble, dataModeType },
    getQueryKeyWithViewId,
  } = useViewContext();
  const { selectedEnsemble: appEnsemble } = useContext(EnsembleContext);
  const selectedEnsemble = viewEnsemble ?? appEnsemble;
  const tenantConfig = currentTenant?.preferences;
  const isStreaming = Boolean(streamingTimeInSeconds);
  const formattedStartTime = startTime?.toISOString() ?? undefined;
  const formattedEndTime = endTime?.toISOString() ?? undefined;
  const { setTagChartData } = useContext(InvestigateContext);

  let interval = dataModeType === DataModeType.RAW ? 0 : IntervalUtil.DEFAULT_INTERVAL;
  if (startTime && endTime) {
    interval =
      dataModeType === DataModeType.RAW
        ? 0
        : dataModeType === DataModeType.RESAMPLED
        ? Math.max(
            60,
            IntervalUtil.fromThresholds(
              startTime,
              endTime,
              tenantConfig?.analyzeTimeThresholds as Array<TimeThreshold>,
            ),
          )
        : IntervalUtil.fromThresholds(
            startTime,
            endTime,
            tenantConfig?.analyzeTimeThresholds as Array<TimeThreshold>,
          );
  }

  const refetchInterval: number | false = isStreaming
    ? IntervalUtil.refetchMsFromInterval(interval)
    : false;

  const queries = folderTags.map(({ folder, tag_name }) => {
    const payload = {
      folder: Number(folder),
      tag_names: [tag_name],
      interval: interval,
    } as unknown as TagResampleMinMaxPayload;

    if (isStreaming) {
      payload.preset = streamingTimeInSeconds;
    } else {
      payload.start_time = formattedStartTime;
      payload.end_time = formattedEndTime;
      payload.start_time = formattedStartTime;
    }
    if (selectedEnsemble?.family_id) {
      payload.ensemble_family_id = selectedEnsemble?.family_id;
    }
    return {
      queryKey: [
        getQueryKeyWithViewId(FOLDER_STRING_DATA_KEY),
        getQueryKeyWithViewId(STRING_TAGS_DATA_KEY),
        folder,
        tag_name,
        selectedEnsemble?.family_id,
      ],
      select: (body: ResampleTagsMinMaxResult) => body.result,
      queryFn: () => resampledTagsMinMaxString(payload, selectedEnsemble?.family_id as string),
      refetchInterval: refetchInterval, // 60 seconds
      retry: isStreaming ? 1 : undefined,
      cacheTime: isStreaming ? interval * 4000 : 4 * 60 * 60 * 1000, // 4 hour,
      staleTime: isStreaming ? interval * 1000 : 60 * 60 * 1000, // 1 hour,
      enabled:
        enabled &&
        ObjectUtils.areNonNull(payload as unknown as Record<string, unknown>, Object.keys(payload)),
      onSuccess: (newData: CRTagData<TimeSeries, Mode, AnomalyRange>[]) => {
        newData.map((tagData) => {
          setTagChartData((prevState) => {
            return {
              ...prevState,
              ...{
                [tagData.tag]: tagData,
              },
            };
          });
        });
      },
    };
  });

  return useQueries(queries);
};
export const useFolderDiscreteTagMinMax = (
  { startTime, endTime, streamingTimeInSeconds, folderTags }: FolderTagMinMaxParams,
  enabled = true,
) => {
  const { currentTenant } = useTenants();
  const {
    viewState: { selectedEnsemble: viewEnsemble, dataModeType },
    getQueryKeyWithViewId,
  } = useViewContext();
  const { selectedEnsemble: appEnsemble } = useContext(EnsembleContext);
  const selectedEnsemble = viewEnsemble ?? appEnsemble;
  const tenantConfig = currentTenant?.preferences;
  const isStreaming = Boolean(streamingTimeInSeconds);
  const formattedStartTime = startTime?.toISOString() ?? undefined;
  const formattedEndTime = endTime?.toISOString() ?? undefined;
  const { setTagChartData } = useContext(InvestigateContext);

  let interval = dataModeType === DataModeType.RAW ? 0 : IntervalUtil.DEFAULT_INTERVAL;
  if (startTime && endTime) {
    interval =
      dataModeType === DataModeType.RAW
        ? 0
        : dataModeType === DataModeType.RESAMPLED
        ? Math.max(
            60,
            IntervalUtil.fromThresholds(
              startTime,
              endTime,
              tenantConfig?.analyzeTimeThresholds as Array<TimeThreshold>,
            ),
          )
        : IntervalUtil.fromThresholds(
            startTime,
            endTime,
            tenantConfig?.analyzeTimeThresholds as Array<TimeThreshold>,
          );
  }

  const refetchInterval: number | false = isStreaming
    ? IntervalUtil.refetchMsFromInterval(interval)
    : false;

  const queries = folderTags.map(({ folder, tag_name }) => {
    const payload = {
      folder: Number(folder),
      tag_names: [tag_name],
      interval: interval,
    } as unknown as TagResampleMinMaxPayload;

    if (isStreaming) {
      payload.preset = streamingTimeInSeconds;
    } else {
      payload.start_time = formattedStartTime;
      payload.end_time = formattedEndTime;
      payload.start_time = formattedStartTime;
    }
    if (selectedEnsemble?.family_id) {
      payload.ensemble_family_id = selectedEnsemble?.family_id;
    }
    return {
      queryKey: [
        getQueryKeyWithViewId(FOLDER_DISCRETE_DATA_KEY),
        getQueryKeyWithViewId(DISCRETE_TAGS_DATA_KEY),
        folder,
        tag_name,
        selectedEnsemble?.family_id,
      ],
      select: (body: ResampleTagsMinMaxResult) => body.result,
      queryFn: () => resampledTagsMinMaxDiscrete(payload, selectedEnsemble?.family_id as string),
      refetchInterval: refetchInterval, // 60 seconds
      retry: isStreaming ? 1 : undefined,
      cacheTime: isStreaming ? interval * 4000 : 4 * 60 * 60 * 1000, // 4 hour,
      staleTime: isStreaming ? interval * 1000 : 60 * 60 * 1000, // 1 hour,
      enabled:
        enabled &&
        ObjectUtils.areNonNull(payload as unknown as Record<string, unknown>, Object.keys(payload)),
      onSuccess: (newData: CRTagData<TimeSeries, Mode, AnomalyRange>[]) => {
        newData.map((tagData) => {
          setTagChartData((prevState) => {
            return {
              ...prevState,
              ...{
                [tagData.tag]: tagData,
              },
            };
          });
        });
      },
    };
  });

  return useQueries(queries);
};

export const useRollingAverageTagsMinMax = (
  {
    tagName,
    startTime,
    endTime,
    windowSizeMinutes,
    useHourlyData,
    viewStartTime,
    viewEndTime,
    dataType,
    exclusions,
    opMode,
  }: TagMinMaxRollingAverageParams,
  enabled = true,
) => {
  const { currentTenant } = useTenants();
  const tenantConfig = currentTenant?.preferences;

  // Calculate interval based on view time range (same logic as useTagResampleMinMax)
  const viewStart = dayjs(viewStartTime);
  const viewEnd = dayjs(viewEndTime);

  let interval = dataType === DataModeType.RAW ? 0 : IntervalUtil.DEFAULT_INTERVAL;
  if (viewStart && viewEnd) {
    interval =
      dataType === DataModeType.RAW
        ? 0
        : dataType === DataModeType.RESAMPLED
        ? Math.max(
            60,
            IntervalUtil.fromThresholds(
              viewStart,
              viewEnd,
              tenantConfig?.analyzeTimeThresholds as Array<TimeThreshold>,
            ),
          )
        : IntervalUtil.fromThresholds(
            viewStart,
            viewEnd,
            tenantConfig?.analyzeTimeThresholds as Array<TimeThreshold>,
          );
  }

  const payload = {
    tag_name: tagName,
    start_time: startTime,
    end_time: endTime,
    window_size_minutes: windowSizeMinutes,
    use_hourly_data: useHourlyData,
    view_start_time: viewStartTime,
    view_end_time: viewEndTime,
    interval: interval,
    exclusions: exclusions,
    op_mode: opMode,
  } as TagMinMaxRollingAveragePayload;

  const key = ['tag_rolling_average', payload];
  return useQuery(key, () => rollingAverageTagsMinMax(payload), {
    enabled:
      enabled &&
      ObjectUtils.areNonNull(payload as unknown as Record<string, unknown>, [
        'tag_name',
        'start_time',
        'end_time',
        'window_size_minutes',
        'use_hourly_data',
        'view_start_time',
        'view_end_time',
      ]),
    select: (body) => body.result,
  });
};

export const useRollingAverageWithAnomalies = (
  {
    tagName,
    startTime,
    endTime,
    viewStartTime,
    viewEndTime,
    windowSizeMinutes,
    upperLimit,
    lowerLimit,
    dataType,
  }: RollingAverageWithAnomaliesParams,
  enabled = true,
  ensembleFamilyId?: string,
) => {
  const { currentTenant } = useTenants();
  const tenantConfig = currentTenant?.preferences;

  // Calculate interval based on view time range (same logic as useRollingAverageTagsMinMax)
  const viewStart = dayjs(viewStartTime);
  const viewEnd = dayjs(viewEndTime);

  let interval = dataType === DataModeType.RAW ? 0 : IntervalUtil.DEFAULT_INTERVAL;
  if (viewStart && viewEnd) {
    interval =
      dataType === DataModeType.RAW
        ? 0
        : dataType === DataModeType.RESAMPLED
        ? Math.max(
            60,
            IntervalUtil.fromThresholds(
              viewStart,
              viewEnd,
              tenantConfig?.analyzeTimeThresholds as Array<TimeThreshold>,
            ),
          )
        : IntervalUtil.fromThresholds(
            viewStart,
            viewEnd,
            tenantConfig?.analyzeTimeThresholds as Array<TimeThreshold>,
          );
  }

  const payload: TagRollingAverageWithAnomaliesPayload = {
    tag_name: tagName,
    start_time: startTime,
    end_time: endTime,
    view_start_time: viewStartTime,
    view_end_time: viewEndTime,
    window_size_minutes: windowSizeMinutes,
    upper_limit: upperLimit,
    lower_limit: lowerLimit,
    interval: interval,
  };

  const key = ['tag_rolling_average_with_anomalies', payload];
  return useQuery(key, () => rollingAverageWithAnomalies(payload, ensembleFamilyId), {
    enabled:
      enabled &&
      ObjectUtils.areNonNull(payload as unknown as Record<string, unknown>, [
        'tag_name',
        'start_time',
        'end_time',
        'view_start_time',
        'view_end_time',
        'window_size_minutes',
        'upper_limit',
        'lower_limit',
        'interval',
      ]),
    select: (body) => body.result,
  });
};

export const useTagMinMax = (
  { startTime, endTime, streamingTimeInSeconds, folder, tag, dataType }: TagMinMaxParams,
  enabled = true,
  ensembleFamilyId?: string,
  ensembleId?: number,
) => {
  const { data, ...rest } = useTagResampleMinMax(
    { startTime, endTime, streamingTimeInSeconds, folder, tags: [tag], dataType },
    enabled,
    ensembleFamilyId,
    ensembleId,
  );

  const prevData = usePrevious(data);
  const [tagData, setTagData] = useState<CRTagData<TimeSeries, Mode, AnomalyRange> | undefined>(
    data?.[0],
  );

  useEffect(() => {
    if (!prevData && data?.length) {
      setTagData(data[0]);
    } else if (data?.length && JSON.stringify(prevData?.[0] ?? {}) !== JSON.stringify(data[0])) {
      setTagData(data[0]);
    }
  }, [data, prevData]);

  return { ...rest, data: tagData, isLoading: rest.isLoading || (!tagData && !rest.isError) };
};

export const useLimitsMinMax = (
  { startTime, endTime, streamingTimeInSeconds, folder, tag, dataType }: TagMinMaxParams,
  enabled = true,
  ensembleFamilyId?: string,
  chartId?: string,
) => {
  const { data, ...rest } = useTagsLimitMinMax(
    { startTime, endTime, streamingTimeInSeconds, folder, tags: [tag], dataType },
    enabled,
    ensembleFamilyId,
    chartId,
  );

  return { ...rest, data, isLoading: rest.isLoading || (!data && !rest.isError) };
};

export const useTagMinMaxV2 = (
  { startTime, endTime, streamingTimeInSeconds, folder, tag, dataType }: TagMinMaxParams,
  enabled = true,
  ensembleFamilyId?: string,
  ensembleId?: number,
  chartId?: string,
) => {
  // An empty tag data is being sent from assess chart content
  const { data, ...rest } = useTagResampleMinMaxV2(
    { startTime, endTime, streamingTimeInSeconds, folder, tags: [tag], dataType },
    enabled,
    ensembleFamilyId,
    ensembleId,
    chartId,
  );

  return { ...rest, data: data, isLoading: rest.isLoading || (!data && !rest.isError) };
};

export const useTagMinMaxString = (
  { startTime, endTime, streamingTimeInSeconds, folder, tag, dataType }: TagMinMaxStringParams,
  enabled = true,
  ensembleFamilyId?: string,
  ensembleId?: number,
) => {
  const tags = Array.isArray(tag) ? tag : [tag];
  const { data, ...rest } = useTagResampleMinMaxString(
    { startTime, endTime, streamingTimeInSeconds, folder, tags, dataType },
    enabled,
    ensembleFamilyId,
    ensembleId,
  );

  const prevData = usePrevious(data);
  const [tagData, setTagData] = useState<CRTagData<TimeSeries, Mode, AnomalyRange> | undefined>(
    data?.[0],
  );

  useEffect(() => {
    if (!prevData && data?.length) {
      setTagData(data[0]);
    } else if (data?.length && JSON.stringify(prevData?.[0] ?? {}) !== JSON.stringify(data[0])) {
      setTagData(data[0]);
    }
  }, [data, prevData]);

  return { ...rest, data: tagData, isLoading: rest.isLoading || (!tagData && !rest.isError) };
};

// TODO: Need to convert into a generic function for all linear, string and discrete tag
export const useTagMinMaxDiscrete = (
  { startTime, endTime, streamingTimeInSeconds, folder, tag, dataType }: TagMinMaxStringParams,
  enabled = true,
  ensembleFamilyId?: string,
  ensembleId?: number,
) => {
  const tags = Array.isArray(tag) ? tag : [tag];
  const { data, ...rest } = useTagResampleMinMaxDiscrete(
    { startTime, endTime, streamingTimeInSeconds, folder, tags, dataType },
    enabled,
    ensembleFamilyId,
    ensembleId,
  );

  const prevData = usePrevious(data);
  const [tagData, setTagData] = useState<CRTagData<TimeSeries, Mode, AnomalyRange> | undefined>(
    data?.[0],
  );

  useEffect(() => {
    if (!prevData && data?.length) {
      setTagData(data[0]);
    } else if (data?.length && JSON.stringify(prevData?.[0] ?? {}) !== JSON.stringify(data[0])) {
      setTagData(data[0]);
    }
  }, [data, prevData]);

  return { ...rest, data: tagData, isLoading: rest.isLoading || (!tagData && !rest.isError) };
};

export const useMultiTagMinMax = (
  { startTime, endTime, streamingTimeInSeconds, folder, tag }: MultiTagMinMaxParams,
  enabled = true,
  ensembleFamilyId?: string,
  ensembleId?: number,
) => {
  const tags = Array.isArray(tag) ? tag : [tag];
  const { data, ...rest } = useTagResampleMinMax(
    { startTime, endTime, streamingTimeInSeconds, folder, tags: tags },
    enabled,
    ensembleFamilyId,
    ensembleId,
  );
  const prevData = usePrevious(data);
  const [tagData, setTagData] = useState<CRTagData<TimeSeries, Mode, AnomalyRange>[] | undefined>(
    data,
  );

  useEffect(() => {
    if (!prevData && data?.length) {
      setTagData(data);
    } else if (data?.length && JSON.stringify(prevData ?? []) !== JSON.stringify(data)) {
      setTagData(data);
    }
  }, [data, prevData]);

  return { ...rest, data: tagData, isLoading: rest.isLoading || (!tagData && !rest.isError) };
};

export const useMultiTagMinMaxString = (
  { startTime, endTime, streamingTimeInSeconds, folder, tag }: MultiTagMinMaxStringParams,
  enabled = true,
  ensembleFamilyId?: string,
  ensembleId?: number,
) => {
  const tags = Array.isArray(tag) ? tag : [tag];
  const { data, ...rest } = useTagResampleMinMaxString(
    { startTime, endTime, streamingTimeInSeconds, folder, tags: tags },
    enabled,
    ensembleFamilyId,
    ensembleId,
  );
  const prevData = usePrevious(data);
  const [tagData, setTagData] = useState<CRTagData<TimeSeries, Mode, AnomalyRange>[] | undefined>(
    data,
  );

  useEffect(() => {
    if (!prevData && data?.length) {
      setTagData(data);
    } else if (data?.length && JSON.stringify(prevData ?? []) !== JSON.stringify(data)) {
      setTagData(data);
    }
  }, [data, prevData]);

  return { ...rest, data: tagData, isLoading: rest.isLoading || (!tagData && !rest.isError) };
};

// export const useTagSearchMinMax = (
//   { startTime, endTime, streamingTimeInSeconds, folder = 0, searchKeys }: TagSearchParams,
//   enabled = true,
// ) => {
//   const { currentTenant } = useTenants();
//   const { getQueryKeyWithViewId } = useViewContext();
//   const tenantConfig = currentTenant?.preferences;
//   const isStreaming = Boolean(streamingTimeInSeconds);

//   const formattedStartTime = startTime?.toISOString() ?? undefined;
//   const formattedEndTime = endTime?.toISOString() ?? undefined;
//   const searchKey = searchKeys?.join(',') ?? undefined;

//   let interval = IntervalUtil.DEFAULT_INTERVAL;
//   if (startTime && endTime) {
//     interval = IntervalUtil.fromThresholds(
//       startTime,
//       endTime,
//       tenantConfig?.analyzeTimeThresholds as Array<TimeThreshold>,
//     );
//   }

//   const payload = {
//     start_time: formattedStartTime,
//     end_time: formattedEndTime,
//     streamingTimeInSeconds,
//     folder,
//     search_key: searchKey,
//     interval,
//   };
//   const key = [getQueryKeyWithViewId(TAG_DATA_KEY), 'tag_search', 'min_max', payload];
//   return useQuery(key, () => searchTagsMinMax(payload), {
//     enabled:
//       enabled &&
//       (ObjectUtils.areNonNull(payload, ['start_time', 'end_time', 'folder', 'search_key']) ||
//         ObjectUtils.areNonNull(payload, ['preset', 'folder', 'search_key'])),
//     retry: isStreaming ? 1 : undefined,
//     refetchInterval: isStreaming ? IntervalUtil.refetchMsFromInterval(interval) : false,
//     select: (body) => body.result,
//     cacheTime: 4 * 60 * 60 * 1000, // 4 hour,
//     staleTime: 60 * 60 * 1000, // 1 hour,
//   });
// };

export const useUnHideTag = () => {
  const queryClient = useQueryClient();
  const { getQueryKeyWithViewId } = useViewContext();

  return useMutation((tagData: HiddenTag) => unHideTag(tagData), {
    onSuccess: async () => {
      queryClient.invalidateQueries(getQueryKeyWithViewId(HIDDEN_TAG_KEY));
      queryClient.invalidateQueries(getQueryKeyWithViewId(ANOMALOUS_FOLDERS_KEY));
      queryClient.invalidateQueries(getQueryKeyWithViewId(FOLDER_KEY));
      queryClient.invalidateQueries(getQueryKeyWithViewId(ANOMALIES_KEY));
      queryClient.invalidateQueries(getQueryKeyWithViewId(TAG_DATA_KEY));
    },
    onError: (e) => {
      console.warn('Failed to unhide', e);
    },
  });
};

export const useHideTag = () => {
  const queryClient = useQueryClient();
  const { getQueryKeyWithViewId } = useViewContext();

  return useMutation((folderData: HiddenTag) => hideTag(folderData), {
    onSuccess: async () => {
      queryClient.invalidateQueries(getQueryKeyWithViewId(HIDDEN_TAG_KEY));
      queryClient.invalidateQueries(getQueryKeyWithViewId(ANOMALOUS_FOLDERS_KEY));
      queryClient.invalidateQueries(getQueryKeyWithViewId(FOLDER_KEY));
      queryClient.invalidateQueries(getQueryKeyWithViewId(ANOMALIES_KEY));
      queryClient.invalidateQueries(getQueryKeyWithViewId(TAG_DATA_KEY));
    },
    onError: (e) => {
      console.warn('Failed to hide tag', e);
    },
  });
};

export const useHiddenTags = () => {
  const { getQueryKeyWithViewId } = useViewContext();
  return useQuery([getQueryKeyWithViewId(HIDDEN_TAG_KEY)], () => fetchAllHiddenTags());
};

export const useTagAutoLabeledEvents = (
  { startTime, endTime, streamingTimeInSeconds, folder = 0, tags }: TagResampleParams,
  enabled = true,
) => {
  const { currentTenant } = useTenants();
  const { getQueryKeyWithViewId } = useViewContext();
  const tenantConfig = currentTenant?.preferences;
  const isStreaming = Boolean(streamingTimeInSeconds);
  const formattedStartTime = startTime?.toISOString() ?? undefined;
  const formattedEndTime = endTime?.toISOString() ?? undefined;

  let interval = IntervalUtil.DEFAULT_INTERVAL;
  if (startTime && endTime) {
    interval = IntervalUtil.fromThresholds(
      startTime,
      endTime,
      tenantConfig?.analyzeTimeThresholds as Array<TimeThreshold>,
    );
  }

  const payload = {
    folder: folder,
    tag_names: tags,
    interval: interval,
  } as TagResampleMinMaxPayload;

  if (isStreaming) {
    payload.preset = streamingTimeInSeconds;
  } else {
    payload.start_time = formattedStartTime;
    payload.end_time = formattedEndTime;
  }

  const key = [getQueryKeyWithViewId('AUTO_LABEL_EVENTS'), payload];
  return useQuery(key, () => getAutoLabeledEvents(payload), {
    enabled:
      enabled &&
      ObjectUtils.areNonNull(payload as unknown as Record<string, unknown>, Object.keys(payload)),
    select: (body) => body.result,
    retry: isStreaming ? 1 : undefined,
    refetchInterval: isStreaming ? IntervalUtil.refetchMsFromInterval(interval) : false,
    cacheTime: isStreaming ? interval * 4000 : 4 * 60 * 60 * 1000, // 4 hour,
    staleTime: isStreaming ? interval * 1000 : 60 * 60 * 1000, // 1 hour,
  });
};

export const useSearchEvents = (
  startTime: string,
  endTime: string,
  tag_name: string,
  searchStartTime: string,
  searchEndTime: string,
) => {
  const { getQueryKeyWithViewId } = useViewContext();
  return useQuery(
    [getQueryKeyWithViewId('TIMESEARCH')],
    () => timeSearch({ startTime, endTime, tag_name, searchStartTime, searchEndTime }),
    {
      cacheTime: 4 * 60 * 60 * 1000, // 4 hour,
      staleTime: 60 * 60 * 1000, // 1 hour,
    },
  );
};

export const useTagResampleMinMax = (
  { startTime, endTime, streamingTimeInSeconds, folder = 0, tags, dataType }: TagResampleParams,
  enabled = true,
  ensembleFamilyId?: string,
  ensembleId?: number,
) => {
  const { currentTenant } = useTenants();
  const {
    viewState: { selectedEnsemble: viewEnsemble },
    getQueryKeyWithViewId,
  } = useViewContext();
  const { selectedEnsemble: appEnsemble } = useContext(EnsembleContext);
  const selectedEnsemble = viewEnsemble ?? appEnsemble;
  const tenantConfig = currentTenant?.preferences;
  const isStreaming = Boolean(streamingTimeInSeconds);
  const formattedStartTime = startTime?.toISOString() ?? undefined;
  const formattedEndTime = endTime?.toISOString() ?? undefined;

  let interval = dataType === DataModeType.RAW ? 0 : IntervalUtil.DEFAULT_INTERVAL;
  if (startTime && endTime) {
    interval =
      dataType === DataModeType.RAW
        ? 0
        : dataType === DataModeType.RESAMPLED
        ? Math.max(
            60,
            IntervalUtil.fromThresholds(
              startTime,
              endTime,
              tenantConfig?.analyzeTimeThresholds as Array<TimeThreshold>,
            ),
          )
        : IntervalUtil.fromThresholds(
            startTime,
            endTime,
            tenantConfig?.analyzeTimeThresholds as Array<TimeThreshold>,
          );
  }

  const payload = {
    folder: folder,
    tag_names: tags,
    interval: interval,
  } as TagResampleMinMaxPayload;

  if (isStreaming) {
    payload.preset = streamingTimeInSeconds;
  } else {
    payload.start_time = formattedStartTime;
    payload.end_time = formattedEndTime;
  }

  if (ensembleId !== null && ensembleId !== undefined) {
    payload.ensemble_id = ensembleId;
  } else if (ensembleFamilyId) {
    payload.ensemble_family_id = ensembleFamilyId;
  }

  if (ensembleId) {
    payload.ensemble_id = ensembleId;
  }

  const key = [
    getQueryKeyWithViewId(TAG_DATA_KEY),
    'tag_resample',
    'min_max',
    payload,
    selectedEnsemble?.family_id,
  ];
  return useQuery(key, () => resampledTagsMinMax(payload, selectedEnsemble?.family_id as string), {
    enabled:
      enabled &&
      ObjectUtils.areNonNull(payload as unknown as Record<string, unknown>, Object.keys(payload)),
    select: (body) => body.result,
    retry: isStreaming ? 1 : undefined,
    refetchInterval: isStreaming ? IntervalUtil.refetchMsFromInterval(interval) : false,
    cacheTime: isStreaming ? interval * 4000 : 4 * 60 * 60 * 1000, // 4 hour,
    staleTime: isStreaming ? interval * 1000 : 60 * 60 * 1000, // 1 hour,
  });
};

export const useTagsLimitMinMax = (
  { startTime, endTime, streamingTimeInSeconds, folder = 0, tags, dataType }: TagResampleParams,
  enabled = true,
  ensembleFamilyId?: string,
  chartId?: string,
) => {
  const { currentTenant } = useTenants();
  const {
    viewState: { selectedEnsemble: viewEnsemble },
    getQueryKeyWithViewId,
  } = useViewContext();
  const { selectedEnsemble: appEnsemble } = useContext(EnsembleContext);
  const selectedEnsemble = viewEnsemble ?? appEnsemble;
  const tenantConfig = currentTenant?.preferences;
  const isStreaming = Boolean(streamingTimeInSeconds);
  const formattedStartTime = startTime?.toISOString() ?? undefined;
  const formattedEndTime = endTime?.toISOString() ?? undefined;

  let interval = dataType === DataModeType.RAW ? 0 : IntervalUtil.DEFAULT_INTERVAL;
  if (startTime && endTime) {
    interval =
      dataType === DataModeType.RAW
        ? 0
        : dataType === DataModeType.RESAMPLED
        ? Math.max(
            60,
            IntervalUtil.fromThresholds(
              startTime,
              endTime,
              tenantConfig?.analyzeTimeThresholds as Array<TimeThreshold>,
            ),
          )
        : IntervalUtil.fromThresholds(
            startTime,
            endTime,
            tenantConfig?.analyzeTimeThresholds as Array<TimeThreshold>,
          );
  }

  const payload = {
    folder: folder,
    tag_names: tags,
    interval: interval,
  } as TagResampleMinMaxPayload;

  if (isStreaming) {
    payload.preset = streamingTimeInSeconds;
  } else {
    payload.start_time = formattedStartTime;
    payload.end_time = formattedEndTime;
  }

  if (ensembleFamilyId) {
    payload.ensemble_family_id = ensembleFamilyId;
  }

  const key = [
    getQueryKeyWithViewId(TAG_DATA_KEY),
    'tags_limit',
    'min_max',
    payload,
    selectedEnsemble?.family_id,
    chartId,
  ];
  //console.log('useTagsLimitMinMax key', key);
  return useQuery(key, () => limitTagsMinMax(payload, selectedEnsemble?.family_id as string), {
    enabled:
      enabled &&
      ObjectUtils.areNonNull(payload as unknown as Record<string, unknown>, Object.keys(payload)),
    select: (body) => body,
    retry: isStreaming ? 1 : undefined,
    refetchInterval: isStreaming ? IntervalUtil.refetchMsFromInterval(interval) : false,
    cacheTime: isStreaming ? interval * 4000 : 4 * 60 * 60 * 1000, // 4 hour,
    staleTime: isStreaming ? interval * 1000 : 60 * 60 * 1000, // 1 hour,
  });
};

export const useTagResampleMinMaxV2 = (
  { startTime, endTime, streamingTimeInSeconds, folder = 0, tags, dataType }: TagResampleParams,
  enabled = true,
  ensembleFamilyId?: string,
  ensembleId?: number,
  chartId?: string,
  onBatch?: (data: CRTagData<TimeSeries, Mode, AnomalyRange>) => void,
) => {
  const { currentTenant } = useTenants();
  const {
    viewState: { selectedEnsemble: viewEnsemble },
    getQueryKeyWithViewId,
  } = useViewContext();
  const { selectedEnsemble: appEnsemble } = useContext(EnsembleContext);
  const selectedEnsemble = viewEnsemble ?? appEnsemble;
  const tenantConfig = currentTenant?.preferences;
  const isStreaming = Boolean(streamingTimeInSeconds);
  const formattedStartTime = startTime?.toISOString() ?? undefined;
  const formattedEndTime = endTime?.toISOString() ?? undefined;
  let interval = dataType === DataModeType.RAW ? 0 : IntervalUtil.DEFAULT_INTERVAL;
  if (startTime && endTime) {
    interval =
      dataType === DataModeType.RAW
        ? 0
        : dataType === DataModeType.RESAMPLED
        ? Math.max(
            60,
            IntervalUtil.fromThresholds(
              startTime,
              endTime,
              tenantConfig?.analyzeTimeThresholds as Array<TimeThreshold>,
            ),
          )
        : IntervalUtil.fromThresholds(
            startTime,
            endTime,
            tenantConfig?.analyzeTimeThresholds as Array<TimeThreshold>,
          );
  }

  const payload = {
    folder: folder,
    tag_names: tags,
    interval: interval,
  } as TagResampleMinMaxPayload;

  if (isStreaming) {
    payload.preset = streamingTimeInSeconds;
  } else {
    payload.start_time = formattedStartTime;
    payload.end_time = formattedEndTime;
  }

  if (ensembleId !== null && ensembleId !== undefined) {
    payload.ensemble_id = ensembleId;
  } else if (ensembleFamilyId) {
    payload.ensemble_family_id = ensembleFamilyId;
  }

  if (ensembleId) {
    payload.ensemble_id = ensembleId;
  }

  const key = [
    getQueryKeyWithViewId(TAG_DATA_KEY),
    'tag_resample',
    'min_max',
    payload,
    selectedEnsemble?.family_id,
    chartId,
  ];

  // Remove direct mutation - this was causing the stack overflow
  // React Query will handle caching automatically

  const queryResp = useQuery(
    key,
    () =>
      resampledTagsMinMaxV2(
        payload,
        selectedEnsemble?.family_id as string,
        onBatch ||
          (() => {
            console.debug('onBatch not implemented');
          }),
      ),
    {
      enabled:
        enabled &&
        ObjectUtils.areNonNull(payload as unknown as Record<string, unknown>, Object.keys(payload)),
      retry: isStreaming ? 1 : undefined,
      refetchInterval: isStreaming ? IntervalUtil.refetchMsFromInterval(interval) : false,
      cacheTime: isStreaming ? interval * 4000 : 4 * 60 * 60 * 1000, // 4 hour,
      staleTime: isStreaming ? interval * 1000 : 60 * 60 * 1000, // 1 hour,
      keepPreviousData: true,
    },
  );
  return queryResp;
};

export const useTagResampleMinMaxString = (
  { startTime, endTime, streamingTimeInSeconds, folder = 0, tags, dataType }: TagResampleParams,
  enabled = true,
  ensembleFamilyId?: string,
  ensembleId?: number,
) => {
  const { currentTenant } = useTenants();
  const {
    viewState: { selectedEnsemble: viewEnsemble },
    getQueryKeyWithViewId,
  } = useViewContext();
  const { selectedEnsemble: appEnsemble } = useContext(EnsembleContext);
  const selectedEnsemble = viewEnsemble ?? appEnsemble;
  const tenantConfig = currentTenant?.preferences;
  const isStreaming = Boolean(streamingTimeInSeconds);
  const formattedStartTime = startTime?.toISOString() ?? undefined;
  const formattedEndTime = endTime?.toISOString() ?? undefined;

  let interval = dataType === DataModeType.RAW ? 0 : IntervalUtil.DEFAULT_INTERVAL;
  if (startTime && endTime) {
    interval =
      dataType === DataModeType.RAW
        ? 0
        : dataType === DataModeType.RESAMPLED
        ? Math.max(
            60,
            IntervalUtil.fromThresholds(
              startTime,
              endTime,
              tenantConfig?.analyzeTimeThresholds as Array<TimeThreshold>,
            ),
          )
        : IntervalUtil.fromThresholds(
            startTime,
            endTime,
            tenantConfig?.analyzeTimeThresholds as Array<TimeThreshold>,
          );
  }

  const payload = {
    folder: folder,
    tag_names: tags,
    interval: interval,
  } as TagResampleMinMaxPayload;

  if (isStreaming) {
    payload.preset = streamingTimeInSeconds;
  } else {
    payload.start_time = formattedStartTime;
    payload.end_time = formattedEndTime;
  }

  if (ensembleFamilyId) {
    payload.ensemble_family_id = ensembleFamilyId;
  }

  if (ensembleId) {
    payload.ensemble_id = ensembleId;
  }

  const key = [
    getQueryKeyWithViewId(TAG_DATA_KEY),
    'tag_resample_string',
    'min_max_string',
    payload,
  ];
  return useQuery(
    key,
    () => resampledTagsMinMaxString(payload, selectedEnsemble?.family_id as string),
    {
      enabled:
        enabled &&
        ObjectUtils.areNonNull(payload as unknown as Record<string, unknown>, Object.keys(payload)),
      select: (body) => body.result,
      retry: isStreaming ? 1 : undefined,
      refetchInterval: isStreaming ? IntervalUtil.refetchMsFromInterval(interval) : false,
      cacheTime: isStreaming ? interval * 4000 : 4 * 60 * 60 * 1000, // 4 hour,
      staleTime: isStreaming ? interval * 1000 : 60 * 60 * 1000, // 1 hour,
    },
  );
};

// TODO: Need to convert into a generic function for all linear, string and discrete tag
export const useTagResampleMinMaxDiscrete = (
  { startTime, endTime, streamingTimeInSeconds, folder = 0, tags, dataType }: TagResampleParams,
  enabled = true,
  ensembleFamilyId?: string,
  ensembleId?: number,
) => {
  const { currentTenant } = useTenants();
  const {
    viewState: { selectedEnsemble: viewEnsemble },
    getQueryKeyWithViewId,
  } = useViewContext();
  const { selectedEnsemble: appEnsemble } = useContext(EnsembleContext);
  const selectedEnsemble = viewEnsemble ?? appEnsemble;
  const tenantConfig = currentTenant?.preferences;
  const isStreaming = Boolean(streamingTimeInSeconds);
  const formattedStartTime = startTime?.toISOString() ?? undefined;
  const formattedEndTime = endTime?.toISOString() ?? undefined;

  let interval = dataType === DataModeType.RAW ? 0 : IntervalUtil.DEFAULT_INTERVAL;
  if (startTime && endTime) {
    interval =
      dataType === DataModeType.RAW
        ? 0
        : dataType === DataModeType.RESAMPLED
        ? Math.max(
            60,
            IntervalUtil.fromThresholds(
              startTime,
              endTime,
              tenantConfig?.analyzeTimeThresholds as Array<TimeThreshold>,
            ),
          )
        : IntervalUtil.fromThresholds(
            startTime,
            endTime,
            tenantConfig?.analyzeTimeThresholds as Array<TimeThreshold>,
          );
  }

  const payload = {
    folder: folder,
    tag_names: tags,
    interval: interval,
  } as TagResampleMinMaxPayload;

  if (isStreaming) {
    payload.preset = streamingTimeInSeconds;
  } else {
    payload.start_time = formattedStartTime;
    payload.end_time = formattedEndTime;
  }

  if (ensembleFamilyId) {
    payload.ensemble_family_id = ensembleFamilyId;
  }

  if (ensembleId) {
    payload.ensemble_id = ensembleId;
  }

  const key = [
    getQueryKeyWithViewId(TAG_DATA_KEY),
    'tag_resample_discrete',
    'min_max_discrete',
    payload,
  ];
  return useQuery(
    key,
    () => resampledTagsMinMaxDiscrete(payload, selectedEnsemble?.family_id as string),
    {
      enabled:
        enabled &&
        ObjectUtils.areNonNull(payload as unknown as Record<string, unknown>, Object.keys(payload)),
      select: (body) => body.result,
      retry: isStreaming ? 1 : undefined,
      refetchInterval: isStreaming ? IntervalUtil.refetchMsFromInterval(interval) : false,
      cacheTime: isStreaming ? interval * 4000 : 4 * 60 * 60 * 1000, // 4 hour,
      staleTime: isStreaming ? interval * 1000 : 60 * 60 * 1000, // 1 hour,
    },
  );
};

export const useEnsembleModelsQuery = () => {
  const { data: ensembleFamilies, ...rest } = useQuery('ensemble', getEnsembleModels, {
    cacheTime: 8 * 60 * 60 * 1000, // 8 hours,
    staleTime: 60 * 60 * 1000, // 1 hour,
  });

  return {
    ensembleFamilies: ensembleFamilies?.result ? ensembleFamilies?.result?.family_list : [],
    ...rest,
  };
};

export const useDefaultEnsembleModels = () => {
  const [search] = useSearchParams();
  const ensembleReference = search.get('reference');
  const startTime = search.get('startTime');
  const endTime = search.get('endTime');

  const { setSelectedEnsemble, setShowEnsembleErrorBanner } = useContext(EnsembleContext);
  const { ensembleFamilies } = useEnsembleModelsQuery();

  // Todo: Show error page if response has "is_anomaly_visible": false
  const { data: ensembleByReference } = useQuery('ensemble_by_reference', () => {
    if (ensembleReference) {
      return getEnsembleIdByReference({ reference: ensembleReference, startTime, endTime });
    }
  });

  const defaultEnsembleFamily = useMemo(() => {
    let ensembleFamily = {} as Ensemble;
    if (ensembleByReference?.result?.family_id) {
      ensembleFamily = ensembleFamilies?.find(
        (ef: Ensemble) => ef.family_id === ensembleByReference?.result?.family_id,
      ) as Ensemble;
      if (!ensembleFamily) {
        setShowEnsembleErrorBanner(true);
      }
    } else if (ensembleFamilies?.length) {
      if (JSON.parse(sessionStorage.getItem('ensemble') as string)?.family_id) {
        ensembleFamily = JSON.parse(sessionStorage.getItem('ensemble') || '{}') as Ensemble;
      } else {
        ensembleFamily = ensembleFamilies?.find((ef: Ensemble) => ef.main_family) as Ensemble;
      }
    }
    return ensembleFamily;
  }, [ensembleByReference, ensembleFamilies, setShowEnsembleErrorBanner]) as Ensemble;

  useEffect(() => {
    if (defaultEnsembleFamily?.family_id) {
      setSelectedEnsemble(defaultEnsembleFamily);
    }
  }, [ensembleFamilies, defaultEnsembleFamily, setSelectedEnsemble]);

  return {
    defaultEnsembleFamily,
  };
};

export const usePredictedData = (
  { startTime, endTime, sources, dataType }: PredictedDataParams,
  enabled = true,
) => {
  const { getQueryKeyWithViewId } = useViewContext();
  const { currentTenant } = useTenants();
  // Filter sources to only include those with ensemble_id
  const filteredSources = sources.filter((source) => source.ensemble_id !== undefined);

  let interval = dataType === DataModeType.RAW ? 0 : IntervalUtil.DEFAULT_INTERVAL;
  if (startTime && endTime) {
    interval =
      dataType === DataModeType.RAW
        ? 0
        : IntervalUtil.fromThresholds(
            startTime,
            endTime,
            currentTenant?.preferences?.analyzeTimeThresholds as Array<TimeThreshold>,
          );
  }

  // If no sources have ensemble_id, disable the query
  const hasValidSources = filteredSources.length > 0;
  const payload: PredictedDataPayload = {
    start_time: startTime?.toISOString() as string,
    end_time: endTime?.toISOString() as string,
    interval: interval,
    sources: sources,
  };
  const key = [getQueryKeyWithViewId('PREDICTED_DATA'), payload];

  const { data, ...rest } = useQuery({
    queryKey: key,
    queryFn: () => predictedTagsMinMaxData(payload),
    enabled: enabled && hasValidSources,
    cacheTime: 4 * 60 * 60 * 1000, // 4 hours
    staleTime: 60 * 60 * 1000, // 1 hour
  });

  const prevData = usePrevious(data);
  const [predictedData, setPredictedData] = useState<TransformedPredictedTag[] | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!hasValidSources) {
      setPredictedData([]);
    } else if (!prevData && data?.result) {
      setPredictedData(data.result);
    } else if (
      data?.result &&
      JSON.stringify(prevData?.result ?? []) !== JSON.stringify(data.result)
    ) {
      setPredictedData(data.result);
    }
  }, [data, prevData, hasValidSources]);

  return {
    ...rest,
    data: predictedData,
    isLoading: (hasValidSources && rest.isLoading) || (!predictedData && !rest.isError),
  };
};
